<template>
    <div class="wb-radio" :class="classes">
        <label :for="inputId" class="wb-radio__label">
            <input :id="inputId"
                   v-model="pick"
                   type="radio"
                   :name="name"
                   :required="required"
                   :value="value"
                   :disabled="disabled"
                   :checked="checked"
                   @change="onChange"
            />

            <div class="wb-radio__container" />
            <slot name="label" :label="label">{{ label }}</slot>
        </label>
    </div>
</template>

<script setup lang="ts">
    import type { WBRadio } from '~/src/shared/components/ui/input/WBRadio/types'

    const props = defineProps<WBRadio>()
    const emit = defineEmits<{
        'update:modelValue': [value: WBRadio['modelValue']]
        change: [value: WBRadio['modelValue']]
    }>()

    const inputId = props.id ?? useId()

    const checked = computed(() => props.modelValue === props.value)

    const classes = computed(() => ({
        _disabled: props.disabled,
        _checked: checked.value
    }))

    const pick = computed({
        get: () => props.modelValue,
        set: (val) => {
            emit('update:modelValue', val)
        }
    })

    function onChange(event: Event) {
        emit('change', (event.target as HTMLInputElement).value)
    }
</script>

<style lang="scss">
    .wb-radio {
        $radio: &;

        &._disabled {
            opacity: 0.7;
            pointer-events: none;
        }

        &__container {
            position: relative;
            width: 18px;
            height: 18px;
            margin-right: 12px;
            border-radius: 50%;
            border: 2px solid var(--grey-200);
            background-color:var(--white);

            &:before {
                content: "";
                position: absolute;
                top: 50%;
                left: 50%;
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background-color: var(--blue-600);
                opacity: 0;
                transform: translate(-50%, -50%);
            }
        }

        &__label {
            display: flex;
            align-items: center;
            cursor: pointer;
        }

        input {
            &:checked + #{$radio}__container {
                border-color: var(--blue-600);

                &:before {
                    opacity: 1;
                }
            }
        }
    }
</style>
